import { useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { localizedMessageOf } from "@/lib/localized-error";
import { usePendingEdits } from "@/lib/pending-edits";
import { logApplicationFailure } from "@/lib/user-error";

// Floating toast at the bottom: appears as soon as any scope has unsaved
// changes. Centralised Verwerfen + Speichern for the whole app.
// Ported node-by-node from workshop/shell/SaveBar.jsx.

export function SaveBar() {
  const intl = useIntl();
  const ctx = usePendingEdits();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // A scope with its own save action is not the bar's to save (ScopeState.ownSaveAction). AppShell's
  // ScrollArea reads the same predicate to reserve room for the bar.
  const dirty = Object.values(ctx?.scopes ?? {}).filter((s) => s.dirty && !s.ownSaveAction);
  if (dirty.length === 0) return null;
  // Also a save started elsewhere: its scope stays dirty until it lands, and saving it again would repeat it.
  const busy = saving || Boolean(ctx?.saving);

  const onSave = async () => {
    setSaving(true);
    setError("");
    try {
      await Promise.all(dirty.map((s) => s.save()));
      // Signal to inline-edit primitives (e.g. EditableRow) that they can
      // auto-collapse - they subscribe to ctx.saveTick.
      ctx?.signalSaved();
    } catch (e) {
      // ScopeState.save contract (lib/pending-edits.tsx): only a LocalizedError is copy for the reader.
      const localized = localizedMessageOf(e);
      if (!localized) logApplicationFailure("save-bar save");
      setError(localized ?? intl.formatMessage({ id: "common.saveFailed", defaultMessage: "Save failed." }));
    } finally {
      setSaving(false);
    }
  };

  const onDiscard = () => {
    setError("");
    dirty.forEach((s) => s.discard());
  };

  // Entries errors ship as "summary\nreason" (use-profile-entries-state.ts); single-line
  // errors just leave `errorDetail` empty.
  const [errorSummary, ...errorDetailParts] = (error ?? "").split("\n");
  const errorDetail = errorDetailParts.join(" ");

  return (
    <div className="fixed bottom-4 sm:bottom-8 left-1/2 z-[900] w-[36rem] max-w-[calc(100vw-2rem)] -translate-x-1/2">
      <div id="save-bar" className="smycles-toast pr-2">
        <span className="min-w-0 flex-1">
          {/* Error inherits the on-accent ink: the toast surface IS the
              signal color, so a text-ink-accent span would be invisible. */}
          {error ? (
            <span className="flex flex-col">
              <span className="font-medium">{errorSummary}</span>
              {errorDetail ? <span className="text-smycles-label opacity-80">{errorDetail}</span> : null}
            </span>
          ) : (
            intl.formatMessage({ id: "unsavedGuard.title", defaultMessage: "Unsaved changes" })
          )}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            id="save-bar-discard"
            variant="secondary"
            onClick={onDiscard}
            disabled={busy}
            className="px-3 py-1 text-smycles-micro disabled:pointer-events-none disabled:opacity-50"
          >
            {intl.formatMessage({ id: "common.discard", defaultMessage: "Discard" })}
          </Button>
          <Button
            id="save-bar-save"
            variant="primary"
            onClick={() => void onSave()}
            disabled={busy}
            className="px-3 py-1 text-smycles-micro disabled:pointer-events-none disabled:opacity-50"
          >
            {busy
              ? intl.formatMessage({ id: "common.saving", defaultMessage: "Saving…" })
              : intl.formatMessage({ id: "common.save", defaultMessage: "Save" })}
          </Button>
        </div>
      </div>
    </div>
  );
}
